"use client"

import { Moon, Sun, Monitor, Palette, Check } from "lucide-react"
import { useTheme } from "next-themes"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { motion } from "framer-motion"
import { useThemeSystem } from "@/components/theme-system"
import { useState } from "react"

export function ThemeToggle() {
  const { setTheme, theme } = useTheme()
  const { currentTheme, availableThemes, setCurrentTheme } = useThemeSystem()
  const [showThemes, setShowThemes] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const getThemeDisplayName = (themeName: string) => {
    const themeNames: Record<string, string> = {
      default: "Professional",
      ocean: "Ocean",
      forest: "Forest",
      sunset: "Sunset",
      midnight: "Midnight",
      sakura: "Sakura",
      aurora: "Aurora",
      ember: "Ember",
      cosmic: "Cosmic",
      monochrome: "Monochrome"
    }
    return themeNames[themeName] || themeName
  }

  const getThemeIcon = (themeName: string) => {
    switch (themeName) {
      case 'ocean':
        return '🌊'
      case 'forest':
        return '🌲'
      case 'sunset':
        return '🌅'
      case 'midnight':
        return '🌙'
      case 'sakura':
        return '🌸'
      case 'aurora':
        return '🌌'
      case 'ember':
        return '🔥'
      case 'cosmic':
        return '🚀'
      case 'monochrome':
        return '⚫'
      default:
        return '💼'
    }
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={(open) => {
      setIsOpen(open)
      if (!open) setShowThemes(false)
    }}>
      <DropdownMenuTrigger asChild>
        <motion.div
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <Button variant="ghost" size="sm" className="glass relative">
            <Sun className="h-4 w-4 rotate-0 scale-100 transition-all duration-300 dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all duration-300 dark:rotate-0 dark:scale-100" />
            <span className="sr-only">Toggle theme</span>
          </Button>
        </motion.div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="theme-bg-card min-w-[200px]">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {!showThemes ? (
            <>
              {/* Light/Dark Mode Section */}
              <DropdownMenuItem 
                onClick={() => setTheme("light")}
                className="hover:bg-muted/50 transition-colors cursor-pointer"
              >
                <Sun className="mr-2 h-4 w-4" />
                Light
                {theme === "light" && <Check className="ml-auto h-4 w-4" />}
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setTheme("dark")}
                className="hover:bg-muted/50 transition-colors cursor-pointer"
              >
                <Moon className="mr-2 h-4 w-4" />
                Dark
                {theme === "dark" && <Check className="ml-auto h-4 w-4" />}
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setTheme("system")}
                className="hover:bg-muted/50 transition-colors cursor-pointer"
              >
                <Monitor className="mr-2 h-4 w-4" />
                System
                {theme === "system" && <Check className="ml-auto h-4 w-4" />}
              </DropdownMenuItem>
              
              <DropdownMenuSeparator />
              
              {/* Theme Palette Section */}
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault()
                  setShowThemes(true)
                }}
                className="hover:bg-muted/50 transition-colors cursor-pointer"
              >
                <Palette className="mr-2 h-4 w-4" />
                Theme
                <span className="ml-auto">›</span>
              </DropdownMenuItem>
            </>
          ) : (
            <>
              {/* Back Button */}
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault()
                  setShowThemes(false)
                }}
                className="hover:bg-muted/50 transition-colors cursor-pointer"
              >
                <span className="mr-2">‹</span>
                Back
              </DropdownMenuItem>
              
              <DropdownMenuSeparator />
              
              {/* Theme Options */}
              {availableThemes.map((themeName) => (
                <DropdownMenuItem
                  key={themeName}
                  onClick={(e) => {
                    e.preventDefault()
                    console.log('Switching to theme:', themeName)
                    setCurrentTheme(themeName)
                    setShowThemes(false)
                    setIsOpen(false)
                  }}
                  className="hover:bg-muted/50 transition-colors cursor-pointer"
                >
                  <span className="mr-2">{getThemeIcon(themeName)}</span>
                  {getThemeDisplayName(themeName)}
                  {currentTheme === themeName && <Check className="ml-auto h-4 w-4" />}
                </DropdownMenuItem>
              ))}
            </>
          )}
        </motion.div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}